/*
 *  -----------------------------------------------------------------------
 *    <copyright file="select-with-categories.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import { orderBy, filterBy, SortDescriptor } from '@progress/kendo-data-query';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import DarkTooltip from '../dark-tool-tip/dark-tool-tip';
import { SelectWithCategoriesProps, SelectWithSearchDataArrayInterface } from './select-with-categories.interface';
import { useBoolean, useTranslation } from 'hooks';
import Constants from 'utils/constants';
import CustomTooltip from '@components/core/custom-tooltip';

import './styles.scss';
import MultiSelectNoResults from '@components/core/multi-select/multi-select-no-results';
import { textToId } from '@utils/automation-test-helpers';

const valueRender = (element: any, value: any) => {
    if (!value) return element;

    const children = (
        <CustomTooltip
            title={value.text}
            key="tooltip-select"
            classes={{
                tooltip: 'tooltip-in-select-comp',
            }}
            disableHoverListener={value?.text.length <= 40 ?? true}
        >
            <span key="select-value">{element.props.children}</span>
        </CustomTooltip>
    );

    return React.cloneElement(element, element.props, children);
};

const SelectWithCategories = ({
    label,
    onChange,
    dataArray,
    labelField,
    fieldSelect,
    customId,
    customClassName,
    showWarning,
    currentState,
    filterable = true,
    disabled,
    searchable = false,
    customLabelId = null,
    showLabels = true,
    splitItemText = true,
    error,
    opened,
    activeOnly,
    initialState,
    appendPopupToSelect,
    addNewAction,
    sortDescriptors,
}: SelectWithCategoriesProps) => {
    const { t } = useTranslation();
    const isFocus = useBoolean(false);

    const setFirstClassForElement = (array: any) => {
        const tempArray = Object.assign([], array);
        const firstActiveIndex = tempArray.findIndex((element: any) => element.active && !element.default);
        const firstInActiveIndex = tempArray.findIndex((element: any) => !element.active && !element.default);
        const lastActiveItemsIndex = _.findLastIndex(
            tempArray,
            (element: any) => element.active && element.id !== initialState,
        );

        tempArray.forEach((item: any, index: number) => {
            if (firstActiveIndex >= 0 && index === firstActiveIndex) {
                item.isFirstActive = true;
                item.isFirstInActive = false;
            } else if (lastActiveItemsIndex >= 0 && index === lastActiveItemsIndex) {
                item.isFirstActive = false;
                item.isFirstInActive = false;
                item.lastActiveElement = true;
            } else if (firstInActiveIndex >= 0 && index === firstInActiveIndex) {
                item.isFirstActive = false;
                item.isFirstInActive = true;
            } else {
                item.isFirstActive = false;
                item.isFirstInActive = false;
            }
        });

        if (firstActiveIndex >= 0) tempArray[firstActiveIndex].isFirstActive = true;
        if (firstInActiveIndex >= 0) tempArray[firstInActiveIndex].isFirstInActive = true;
        if (lastActiveItemsIndex >= 0) tempArray[lastActiveItemsIndex].lastActiveElement = true;
        return tempArray;
    };

    const sortData = (data: SelectWithSearchDataArrayInterface[], filter: boolean, sort?: SortDescriptor[]) => {
        const sorts = sort ? [...sort] : [];
        if (filter) sorts.unshift({ field: 'default', dir: 'desc' }, { field: 'active', dir: 'desc' });
        const tempSortedOptions = orderBy(data, sorts);

        return filter ? setFirstClassForElement(tempSortedOptions) : tempSortedOptions;
    };

    const [sortedOptions, setSortedOptions] = useState(sortData(dataArray, filterable, sortDescriptors));
    const [filteredOptions, setFilteredOptions] = useState(sortedOptions);

    useEffect(() => {
        setFilteredOptions(sortedOptions);
    }, [sortedOptions]);

    useEffect(() => {
        setSortedOptions(sortData(dataArray, filterable, sortDescriptors));
    }, [dataArray, filterable, sortDescriptors]);

    const filterData = (filter: any) => {
        const data = sortedOptions.slice();
        return filterBy(data, filter);
    };

    const filterChange = (event: any) => {
        const newData =
            event.filter.value.length > 0
                ? setFirstClassForElement(filterData(event.filter))
                : setFirstClassForElement(sortedOptions.slice());

        setFilteredOptions(newData);
    };

    const getValue = (id: string, options: any) =>
        options?.find((option: any) => option.id === id) ||
        options?.find((option: any) => option.id === Constants.ObjectId.Empty);

    const classNamesLabel = isFocus.value ? 'on-focus' : disabled ? 'disabled' : null;

    const checkIfDefault = (value: string | null | undefined) =>
        value === null ||
        !value ||
        !value.toString().length ||
        value === Constants.ObjectId.Empty ||
        value === 'NONE' ||
        filteredOptions?.findIndex((item: any) => item.id === value) < 0;

    const getData = () =>
        activeOnly
            ? filteredOptions.filter(
                  (option: SelectWithSearchDataArrayInterface) => option.active || option.id === initialState,
              )
            : filteredOptions;

    const getOptionItem = (text: string, children: any) => {
        const splittedTextArray: string[] = text.split('|');

        if (splitItemText && splittedTextArray.length > 1) {
            return (
                <span className="splitter-options">
                    <DarkTooltip
                        title={splittedTextArray[0].trim()}
                        classes={{ popper: 'always-visible' }}
                        placement="top"
                        arrow
                    >
                        <span>{splittedTextArray[0].trim()}</span>
                    </DarkTooltip>
                    <span>{splittedTextArray[1].trim()}</span>
                </span>
            );
        }
        return <span>{children}</span>;
    };

    const formControlId = customId || `select-${fieldSelect}`;

    return (
        <FormControl variant="outlined" className={customClassName} id={formControlId} data-testid={formControlId}>
            {label && (
                <span
                    id={customLabelId}
                    className={`select-with-categories-label ${classNamesLabel} ${error ? 'error' : ''}`}
                >
                    {label}
                </span>
            )}
            <DropDownList
                disabled={disabled}
                opened={opened}
                value={getValue(currentState, filteredOptions)}
                onChange={(event: any) => {
                    const { id } = event.target.value;
                    const newEvent = { target: { value: id } };
                    newEvent.target.value = id;

                    onChange(fieldSelect)(newEvent);
                }}
                ref={(instance: any) => {
                    (window as any)[formControlId] = instance;
                }}
                className={`select-with-categories ${checkIfDefault(currentState) ? 'default' : ''} ${
                    error ? 'error' : ''
                }`}
                data={getData()}
                textField="text"
                filterable={searchable}
                onFilterChange={filterChange}
                onFocus={() => {
                    isFocus.setTrue();
                }}
                onBlur={() => {
                    isFocus.setFalse();
                }}
                popupSettings={{
                    height: '212px',
                    ...(appendPopupToSelect ? { appendTo: window.document.getElementById(formControlId) } : {}),
                }}
                valueRender={valueRender}
                listNoDataRender={() => <MultiSelectNoResults addNewAction={addNewAction} />}
                itemRender={(li, itemProps) => {
                    const props = {
                        ...li.props,
                        'data-testid': `dropdown-item-${textToId(itemProps.dataItem.text, itemProps.index)}`,
                    };

                    if (!itemProps.dataItem.active && !itemProps.dataItem.default && itemProps.dataItem.hidden)
                        props.className = `k-item ${filterable ? 'hidden' : ''}`;

                    if (!itemProps.dataItem.active && !itemProps.dataItem.default && !itemProps.dataItem.hidden)
                        props.className = `k-item ${filterable ? 'disabled' : ''}`;

                    if (
                        (itemProps.dataItem.active || itemProps.dataItem.id === initialState) &&
                        !itemProps.dataItem.default &&
                        showLabels
                    )
                        props.className = `k-item ${itemProps.dataItem.lastActiveElement ? 'last-active' : null}`;

                    const itemChildren = filterable ? (
                        <>
                            <span
                                className={
                                    (itemProps.dataItem.isFirstActive || itemProps.dataItem.isFirstInActive) &&
                                    showLabels
                                        ? 'first-element-in-category'
                                        : null
                                }
                            >
                                {li.props.children}
                            </span>
                            {itemProps.dataItem.isFirstActive && showLabels && (
                                <span className="sub-header-select">{t('active')}</span>
                            )}
                            {itemProps.dataItem.isFirstInActive && showLabels && (
                                <span className="sub-header-select">{t('inactive')}</span>
                            )}
                        </>
                    ) : (
                        getOptionItem(itemProps.dataItem.text, li.props.children)
                    );

                    return React.cloneElement(li, props, itemChildren);
                }}
            />
            {error && <p className="error-message">{error}</p>}
            {showWarning && (
                <p className="warning-message">
                    {t('current')} {t(`ra:${labelField}`)} {t('ra:isInactiveMessage')}
                </p>
            )}
        </FormControl>
    );
};

export default SelectWithCategories;
